import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import {
  GET_PERMISSIONS,
  ELASTIC_SEARCH_BASE_URL,
  IS_ELASTIC_SEARCH_ON,
} from "../../../Constants/Config";
import { useAuthDetails } from "../../../Common/cookiesHelper";
import { ToastifyAlert } from "../../../CommonComponents/ToastifyAlert";
import CryptoJS from "crypto-js";
import { SECRET_KEY } from "../../../Constants/Config";
import { ciUrl } from "../../../Constants/nodeCi";

const normalizePermissionList = (permissions) => {
  if (Array.isArray(permissions)) {
    return permissions.map((code) => String(code).trim()).filter(Boolean);
  }
  if (typeof permissions === "string") {
    return permissions
      .split(",")
      .map((code) => code.trim())
      .filter(Boolean);
  }
  return null;
};

export const fetchPermissions = createAsyncThunk(
  "permissions/fetchPermissions",
  async (requestData, { rejectWithValue }) => {
    const { forceRefresh: _forceRefresh, ...permissionRequest } =
      requestData || {};
    const {
      LoginGetDashBoardRecordJson: {
        data: { merchant_id },
      },
      userTypeData: { token, ...newData },
    } = useAuthDetails();
    try {
      const response = await axios.post(
        ciUrl(GET_PERMISSIONS),
        { ...permissionRequest, ...newData, merchant_id },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const permissions = normalizePermissionList(response?.data?.permissions);
      if (!permissions) {
        return rejectWithValue("Error fetching permissions");
      }
      return permissions;
    } catch (error) {
      ToastifyAlert(error.response.data.message, "warn");
      return rejectWithValue(
        error.response?.data?.message || "Error fetching permissions"
      );
    }
  },
  {
    // Dedupe concurrent dispatches: several components fetch permissions on
    // mount (a single screen can mount more than one). If a request is already
    // in flight, skip the duplicate — the in-flight request populates Redux for
    // all callers. forceRefresh still runs, because a save must read the
    // permissions written by that save, not a request that started before it.
    condition: (requestData, { getState }) => {
      if (requestData?.forceRefresh) return true;
      if (getState().permissions?.status === "loading") {
        return false;
      }
    },
  }
);

export const CheckElasticConnection = createAsyncThunk(
  "permissions/CheckElasticConnection",
  async (data, { rejectWithValue }) => {
    try {
      if (IS_ELASTIC_SEARCH_ON) {
        const checkConnection = await axios.get(ELASTIC_SEARCH_BASE_URL, {
          timeout: 5000,
        });
        if (checkConnection && checkConnection.status === 200) {
          return true;
        }
      } else {
        return false;
      }
    } catch (error) {
      console.log(
        "Elastic server is down, falling back to BASE_URL",
        error.message
      );
      return null;
    }
  }
);

const permissionsSlice = createSlice({
  name: "permissionsData",
  initialState: {
    permissions: [],
    status: "idle",
    isElasticConnected: null,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPermissions.pending, (state, action) => {
        state.status = "loading";
        state.latestRequestId = action.meta.requestId;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        // A request that started before a save can resolve after the refresh
        // and would put the old codes back.
        if (state.latestRequestId !== action.meta.requestId) return;
        state.status = "succeeded";
        state.permissions = action.payload;
        const encryptedPermissions = CryptoJS.AES.encrypt(
          action.payload.join(","),
          SECRET_KEY
        ).toString();
        localStorage.setItem("permissions", encryptedPermissions);
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        if (action.meta.condition) return;
        if (state.latestRequestId !== action.meta.requestId) return;
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(CheckElasticConnection.fulfilled, (state, action) => {
        state.isElasticConnected = action.payload ?? false;
      });
  },
});

export default permissionsSlice.reducer;
